import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Input, Switch, Textarea } from 'tdesign-react';

import NovaDialog from '@/components/dialog';
import { labelDom } from '@/components/Label';
import { MissionType } from '@/const/mission';
import { isRelease, SEARCH_ID, SEARCH_RELEASE } from '@/const/search';
import { PageIds } from '@/const/tip';
import { Branch, CompletionTipType, CompletionTipValue, IMission, IScene, SceneType } from '@/interface';
import { setKeyIndex } from '@/reducer/arrayReducers';
import { useAppDispatch, useTypedSelector } from '@/store';
import { getStoryNumberListStore } from '@/store/modules/storyNumber';

import { BranchArray, defaultValue } from './Branch';
import ItemCardContainer, { CardData } from './Branch/numberSystem/cards';
import styles from './index.module.less';
import { BranchSelect } from './SelectBranch/index';

const OptionName = {
  NO_OPTION: '无选项',
  RIGHT_NOW: '现在就去',
};

export interface IProps {
  value: IMission['completionTip'];
  disabled?: boolean;
  cparams: {
    completionTip: {
      visible: boolean;
      render: {
        title: boolean;
        tip: boolean;
        multiBranch: boolean;
        singleBranch: boolean;
      };
      required: {
        title: boolean;
        multiBranch: boolean;
        singleBranch: boolean;
        tip: boolean;
      };
    }
    missionType?: MissionType;
    sceneList?: { name: string, id: string }[];
    sceneType: SceneType;
    isFinal: boolean;
    passConditions: IScene['passConditions']
  };
  onChange: (value: IMission['completionTip']) => void;
}

const CustomBranch: React.FC<IProps> = (props) => {
  const {
    onChange,
    value,
    disabled = false,
    cparams,
  } = props;

  const { sceneList = [], completionTip, isFinal, passConditions = [] } = cparams || {};
  const { render, required, visible } = completionTip || {};
  const { tip, multiBranch = [], title } = value || {};
  const [tipVisible, setTipVisible] = useState(false);

  const options = sceneList?.map(item => ({ label: item.name, value: item.id }));

  const [searchParams] = useSearchParams();
  const storyId = searchParams.get(SEARCH_ID) || '';
  const release = isRelease(searchParams.get(SEARCH_RELEASE));
  const dispatch = useAppDispatch();

  const { loading: storyNumberLoading, list: storyNumberList } = useTypedSelector(store => store.storyNumber);

  useEffect(() => {
    if (storyId) {
      dispatch(getStoryNumberListStore({ storyId, release, page: 1, size: 999 }));
    }
  }, [storyId, release]);

  const handleChange = (key: string, val: string | boolean) => {
    onChange({ ...props.value, [key]: val });
  };

  const closeTip = () => {
    setTipVisible(false);
  };

  const confirmTip = () => {
    onChange({
      ...props.value,
      multiBranch: [{ optionName: OptionName.NO_OPTION }],
      tipType: CompletionTipType.COMPLETIONTYPE_NEXTSCENE,
    });
    closeTip();
  };

  const handleSwitch = (hasOption: boolean) => {
    if (hasOption) {
      onChange({
        ...props.value,
        multiBranch: setKeyIndex([defaultValue], true),
        tipType: CompletionTipType.COMPLETIONTYPE_MULTIBRANCH,
      });
    } else {
      setTipVisible(true);
    }
  };

  const handleBranchChange = (value: Branch[]) => {
    onChange({
      ...props.value,
      multiBranch: value,
      tipType: CompletionTipType.COMPLETIONTYPE_MULTIBRANCH,
    });
  };

  const selectChange = (params: { label: string, value: string }) => {
    const val = params.value;
    if (val === CompletionTipValue[CompletionTipType.COMPLETIONTYPE_SINGLEBRANCH]) {
      onChange({
        ...props.value, tipType: CompletionTipType.COMPLETIONTYPE_SINGLEBRANCH,
        multiBranch: [],
      });
      return;
    }
    if (val === CompletionTipValue[CompletionTipType.COMPLETIONTYPE_TOBECONTINUED]) {
      onChange({
        ...props.value, tipType: CompletionTipType.COMPLETIONTYPE_TOBECONTINUED,
        multiBranch: [],
      });
      return;
    }
    if (val === CompletionTipValue[CompletionTipType.COMPLETIONTYPE_END]) {
      onChange({
        ...props.value, tipType: CompletionTipType.COMPLETIONTYPE_END,
        multiBranch: [],
      });
      return;
    }
    onChange({
      ...props.value, tipType: CompletionTipType.COMPLETIONTYPE_MULTIBRANCH,
      multiBranch: [{
        sceneId: val as string,
        sceneName: params.label,
        optionName: OptionName.RIGHT_NOW,
      }],
    });
  };

  const handleOnBranchNumCond = (val: CardData[]) => {
    onChange({
      ...props.value,
      nonBranchNumCond: {
        useOptionCond: true,
        numChange: val,
      },
    });
  };

  const handleOnBranchNumCondSwitch = (val: boolean) => {
    onChange({
      ...props.value,
      nonBranchNumCond: {
        ...props.value?.nonBranchNumCond,
        useOptionCond: val,
      },
    });
  };

  const getSingValue = () => {
    let selectValue = '';
    if (isFinal && (
      value?.tipType !== CompletionTipType.COMPLETIONTYPE_TOBECONTINUED
      && value?.tipType !== CompletionTipType.COMPLETIONTYPE_END
    )) {
      return CompletionTipValue[CompletionTipType.COMPLETIONTYPE_TOBECONTINUED];
    }
    if (value?.tipType === CompletionTipType.COMPLETIONTYPE_END) {
      selectValue = CompletionTipValue[CompletionTipType.COMPLETIONTYPE_END];
    }
    if (value?.tipType === CompletionTipType.COMPLETIONTYPE_TOBECONTINUED) {
      selectValue = CompletionTipValue[CompletionTipType.COMPLETIONTYPE_TOBECONTINUED];
    }
    if (value?.tipType === CompletionTipType.COMPLETIONTYPE_SINGLEBRANCH) {
      if (value?.multiBranch?.length && value?.multiBranch[0]?.sceneName) {
        selectValue = value?.multiBranch[0]?.sceneId || '';
      } else {
        selectValue = CompletionTipValue[CompletionTipType.COMPLETIONTYPE_SINGLEBRANCH];
      }
    }
    return selectValue;
  };

  const label = {
    title: labelDom({
      field: {
        props: 'title',
        label: '标题',
      },
      initFormData: {},
      requiredItem: required?.title,
    }),
    tip: labelDom({
      field: {
        props: 'tip',
        label: '提示文案',
      },
      initFormData: {},
      requiredItem: false,
      pageId: PageIds.MISSION_EVENT_CONTENT,
    }),
    select: labelDom({
      field: {
        props: 'multiBranch',
        label: '选项',
      },
      requiredItem: required?.multiBranch,
      initFormData: {},
    }),
  };

  if (!visible) return null;

  const dialogConfig = {
    visible: tipVisible,
    onClose: closeTip,
    theme: 'warning' as 'warning',
    header: '提示',
    placement: 'center' as 'center',
    style: { maxWidth: 400 },
    cancelBtn: null,
    confirmBtn: <>
      <Button theme='default' onClick={closeTip}>
        取消
      </Button>
      <Button
        theme='primary'
        onClick={confirmTip}
      >
        确认
      </Button>
    </>,
  };

  return (
    <div
      className={classNames(
        'custom-form-group',
        { [styles.none]: !visible },
        styles.group,
      )}
    >
      <div className={styles.right}>
        {
          render?.multiBranch && <div className={styles.openOptions}>
            <div className={styles.label}>
              <span className={styles.label}>开启引导选项</span>
            </div>
            <Switch
              disabled={disabled}
              value={value?.tipType !== CompletionTipType.COMPLETIONTYPE_NEXTSCENE}
              onChange={v => handleSwitch(v)}
            />
          </div>
        }
        {
          value?.tipType === CompletionTipType.COMPLETIONTYPE_NEXTSCENE
            ? <div>
              <div className={styles.endNoBranchCondHeader}>
                <div>本幕结束后数值变化</div>
                <div>
                  <Switch
                    disabled={disabled}
                    value={value?.nonBranchNumCond?.useOptionCond}
                    onChange={v => handleOnBranchNumCondSwitch(v)}
                  />
                </div>
              </div>
              <ItemCardContainer
                label=''
                fileName={'numChange'}
                disabled={disabled || !value?.nonBranchNumCond?.useOptionCond}
                value={value?.nonBranchNumCond?.numChange || []}
                leftStyle={{
                  width: 0,
                  margin: 0,
                }}
                onChange={val => handleOnBranchNumCond(val)}
              />
            </div>
            : null
        }
        {
          render?.singleBranch // 特殊项-结局幕，展示选项为title 但是数据结构上是tipType
            ? <div className={styles.clueInput}>
              <div className={styles.label}>
                <span className={styles.label}>{'标题'}</span>
              </div>
              <>
                <BranchSelect
                  options={[]}
                  onChange={selectChange}
                  value={getSingValue()}
                  end={true}
                  disabled={disabled}
                />
              </>
            </div> : null
        }
        {
          render?.title && <div className={styles.clueInput}>
            <div className={styles.label}>
              <span className={styles.label}>{label.title}</span>
            </div>
            <Input
              status="default"
              value={title}
              onChange={value => handleChange('title', value)}
              className={styles.input}
              disabled={disabled}
              placeholder="请输入"
            />
            {required?.title && !title?.trim() ? (
              <span
                className={classNames(styles.errors, 'nova-text-error')}
              >
                请填写标题
              </span>
            ) : null}
          </div>
        }
        {
          render?.tip && <div className={styles.clue}>
            <div className={styles.label}>
              <span className={styles.label}>{label.tip}</span>
            </div>
            <Textarea
              value={tip}
              onChange={value => handleChange('tip', value)}
              disabled={disabled}
              placeholder="请输入"
              autosize={{ minRows: 6, maxRows: 6 }}
            />
            {required?.tip && !tip?.trim() ? (
              <span
                className={classNames(styles.errors, 'nova-text-error')}
              >
                请填写提示文案
              </span>
            ) : null}
          </div>
        }
        {
          render?.multiBranch
          && value?.tipType !== CompletionTipType.COMPLETIONTYPE_NEXTSCENE
          && <div className={styles.clue}>
            <div className={styles.label}>
              <span className={styles.label}>{label.select}</span>
            </div>
            <div className={styles.branch}>
              <>
                <BranchArray
                  value={multiBranch}
                  onChange={handleBranchChange}
                  options={options}
                  disabled={disabled}
                  passConditions={passConditions}
                  storyNumber={
                    {
                      storyNumberList,
                      storyNumberLoading,
                    }
                  }
                />
              </>
            </div>
          </div>
        }
      </div>

      <NovaDialog dialogConfig={dialogConfig}>
        <div className={'nova-save-tips'}>
          关闭选项后，您当前已编辑的「过关剧情」将被清空。请确认是否继续？
        </div>
      </NovaDialog>
    </div>
  );
};

export default CustomBranch;
